import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Tooltip } from '@mui/material';
import {
  MunicipalNumberList,
  ResearchFilterMunicipalNumberVM,
} from 'civil-status/view-models/municipal-number';
import {
  getNameStateNumberMunicipal,
  IdStateNumberMunicipal,
} from 'civil-status/constants/state-municipal-number';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { transformerResearchMunicipalNumber } from 'civil-status/utils/transformer-research-municipal-number';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

interface TableDeclarationsProps {
  list?: Array<MunicipalNumberList>;
  filterSelected?: ResearchFilterMunicipalNumberVM;
  getListData: (
    values: ResearchFilterMunicipalNumberVM,
    data: string,
    item?: MunicipalNumberList,
    state?: string
  ) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
  isRequest?: boolean;
}

const TableDeclarationsComponent = ({
  list,
  filterSelected,
  getListData,
  tableProps = {},
  setTableProps,
  isRequest = false,
}: TableDeclarationsProps): ReactElement => {
  const onUses = useHttpClient(`${__ACT_API_URL__}/numerazione-comunale/utilizza`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onDiscard = useHttpClient(`${__ACT_API_URL__}/numerazione-comunale/scarta`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onCheckPress = useEventCallback((item: MunicipalNumberList) => {
    if (!item.id || !item.number) {
      return;
    }

    (
      onUses({
        data: {
          idPrenotazioneAtti: item.id,
          numeroAttoComunale: item.number,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      const queryString = transformerResearchMunicipalNumber(filterSelected || {});

      getListData(filterSelected || {}, queryString, item, IdStateNumberMunicipal.Utilizzato);
    });
  });

  const onClearPress = useEventCallback((item: MunicipalNumberList) => {
    if (!item.id || !item.number) {
      return;
    }

    (
      onDiscard({
        data: {
          idPrenotazioneAtti: item.id,
          numeroAttoComunale: item.number,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      const queryString = transformerResearchMunicipalNumber(filterSelected || {});

      getListData(filterSelected || {}, queryString, item, IdStateNumberMunicipal.Scartato);
    });
  });

  const body: Array<MunicipalNumberList> = useMemo(() => {
    return (list || []).map((item) => {
      return {
        ...item,
        state: getNameStateNumberMunicipal(item.state),
        action: (
          <div className="pmi-d-flex">
            <Tooltip arrow title="Segna come utilizzato">
              <span>
                <IconButton
                  color="success"
                  onClick={() => {
                    onCheckPress(item);
                  }}
                  aria-label="Segna come utilizzato"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Segna come scartato">
              <span>
                <IconButton
                  onClick={() => {
                    onClearPress(item);
                  }}
                  aria-label="Segna come scartato"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                  color="error"
                >
                  <ClearIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        ),
      };
    });
  }, [list]);

  const onChangePage = useEventCallback((page: number) => {
    if (!isRequest) {
      const queryString = transformerResearchMunicipalNumber(filterSelected || {});

      getListData(filterSelected || {}, queryString);
    }

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    if (!isRequest) {
      const queryString = transformerResearchMunicipalNumber(filterSelected || {});

      getListData(filterSelected || {}, queryString);
    }

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista numerazione comunale"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || body?.length || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
        />
      </Grid>
    </Grid>
  );
};

TableDeclarationsComponent.displayName = 'Icaro Table component';

const TableMunicipalNumber = memo(TableDeclarationsComponent);

export default TableMunicipalNumber;
